import {TKey} from '@/translations/i18n'
import {AppliedFiltersPayload} from '@/features/exports/types'
import {humanizeColumnKey} from '@/features/exports/utils/columns'
import {workSectorToLabel, workServiceToLabel} from '@utilities/helpers.ts'
import {columnNameToLabel as patientsColumnNameToLabel} from '@/features/patients/utils'
import {
    columnNameToLabel as projectsColumnNameToLabel,
    projectAssistiveRegimeToLabel,
    projectProgressStatusToLabel,
    projectSectorToLabel,
    projectStatusToLabel,
    projectTypeToLabel
} from '@/features/projects/utils'
import {columnNameToLabel as usersColumnNameToLabel, userStatusToLabel} from '@/features/users/utils'
import {
    treatmentRequestColumnToLabel,
    authorizationToLabel,
    rejectedReasonToLabel,
    treatmentRequestSectorToLabel,
    treatmentRequestStatusToLabel,
    typeToLabel as treatmentRequestTypeToLabel
} from '@/features/treatment-requests/utils'
import {accessTypeToLabel, accessesStatusToLabel} from '@/features/accesses/utils'
import {agendaEventStatusMapToLabel} from '@/features/agenda/utils'

/** Minimal translator shape (matches the `t` returned by `useTranslation`). */
type TranslateFn = (key: TKey) => string

/** One rendered applied-filter, ready to display as a "field: operator value" badge. */
export interface AppliedFilterSummaryItem {
    /** Localised field name (e.g. "Servizio"). */
    field: string
    /** Localised operator (e.g. "Diverso da"); empty when the operator is unknown. */
    operator: string
    /** Localised, comma-joined value(s) (e.g. "Aperto, In apertura"); empty for valueless operators. */
    value: string
}

const YES_KEY: TKey = 'commons:yes'
const NO_KEY: TKey = 'commons:no'

/**
 * Operator → i18n key, mirroring `filterOperatorToTKey` in the table-filters component so the text
 * reads with the same wording as the filter UI (e.g. `not_in` → "Diverso da"). Both forms are
 * accepted: the filter-UI form (`!=`, `not in`, `>=`) and the normalised form the backend stores in
 * `appliedFilters` (`not_eq`, `not_in`, `gte` — see data-model.md). `is`/`is not` are not surfaced.
 */
const OPERATOR_LABEL: Record<string, TKey> = {
    // filter-UI form
    '<': 'table_filters:operator_less',
    '<=': 'table_filters:operator_less_equal',
    '=': 'table_filters:operator_equal',
    '!=': 'table_filters:operator_not_equal',
    '>=': 'table_filters:operator_greater_equal',
    '>': 'table_filters:operator_greater',
    in: 'table_filters:operator_equal',
    'not in': 'table_filters:operator_not_equal',
    empty: 'table_filters:operator_empty',
    'not empty': 'table_filters:operator_not_empty',
    like: 'table_filters:operator_like',
    // normalised backend form
    eq: 'table_filters:operator_equal',
    not_eq: 'table_filters:operator_not_equal',
    not_in: 'table_filters:operator_not_equal',
    gt: 'table_filters:operator_greater',
    gte: 'table_filters:operator_greater_equal',
    lt: 'table_filters:operator_less',
    lte: 'table_filters:operator_less_equal',
    not_empty: 'table_filters:operator_not_empty'
}

/** Operators that carry no value (the label is self-contained, e.g. "Solo vuoto"). */
const VALUELESS_OPERATORS = new Set(['empty', 'not empty', 'not_empty'])

/**
 * Per-entity map from a filter field key to the same i18n key the data tables use for that column,
 * so the summary reads with the real application labels. Keys missing here (or whose entity has no
 * map) fall back to {@link humanizeColumnKey}.
 */
const FIELD_LABEL_BY_ENTITY: Partial<Record<string, Record<string, TKey | null>>> = {
    patients: patientsColumnNameToLabel,
    projects: projectsColumnNameToLabel,
    users: usersColumnNameToLabel,
    treatment_requests: treatmentRequestColumnToLabel
}

/** Sector values can be either the generic work-sector set or the richer project-sector set. */
const sectorValueToLabel = {...workSectorToLabel, ...projectSectorToLabel}

/**
 * Per-entity map from a filter field key to the value→i18n-key map used by that entity's table
 * filters, so enum values render with their localised label (e.g. `open` → "Aperto"). Resolved-id
 * values arrive as `{id, label}` and are handled separately; booleans render as Sì/No.
 */
const VALUE_LABEL_BY_ENTITY: Partial<Record<string, Record<string, Record<string, TKey>>>> = {
    patients: {
        serviceType: workServiceToLabel,
        projectStatus: projectStatusToLabel
    },
    projects: {
        status: projectStatusToLabel,
        projectStatus: projectStatusToLabel,
        type: projectTypeToLabel,
        progressStatus: projectProgressStatusToLabel,
        sector: sectorValueToLabel,
        serviceType: workServiceToLabel,
        assistiveRegime: projectAssistiveRegimeToLabel
    },
    users: {
        status: userStatusToLabel,
        sector: workSectorToLabel
    },
    treatment_requests: {
        status: treatmentRequestStatusToLabel,
        type: treatmentRequestTypeToLabel,
        authorization: authorizationToLabel,
        rejectedReason: rejectedReasonToLabel,
        sector: treatmentRequestSectorToLabel,
        serviceType: workServiceToLabel
    },
    patient_events: {
        status: agendaEventStatusMapToLabel
    }
}

/** All `accesses_*` export entities share the accesses table's value maps. */
const ACCESSES_VALUE_LABELS: Record<string, Record<string, TKey>> = {
    status: accessesStatusToLabel,
    type: accessTypeToLabel
}

const getValueLabels = (entity: string, key: string): Record<string, TKey> | undefined => {
    if (entity.startsWith('accesses') || entity === 'activity_report') return ACCESSES_VALUE_LABELS[key]
    return VALUE_LABEL_BY_ENTITY[entity]?.[key]
}

/** Localise a single filter field name, reusing the data-table column translations. */
const resolveFieldLabel = (entity: string, key: string, t: TranslateFn): string => {
    const labelKey = FIELD_LABEL_BY_ENTITY[entity]?.[key]
    return labelKey ? t(labelKey) : humanizeColumnKey(key)
}

/**
 * Render a single (non-array) filter value to localised text.
 * - resolved-id fields arrive as `{ id, label }` → show the label, never the raw id (FR-009)
 * - booleans (and their `'true'`/`'false'`/`'yes'`/`'no'` string forms) → Sì/No
 * - enum scalars → the table-filter translation for that value
 * - everything else (dates, numbers, free text) → shown as-is
 */
const resolveScalarValue = (entity: string, key: string, value: unknown, t: TranslateFn): string => {
    if (value === null || value === undefined) return ''
    if (typeof value === 'object') {
        const label = (value as Record<string, unknown>).label
        return typeof label === 'string' ? label : ''
    }
    if (typeof value === 'boolean') return t(value ? YES_KEY : NO_KEY)

    const valueLabels = getValueLabels(entity, key)
    if (typeof value === 'string' && valueLabels && value in valueLabels) {
        return t(valueLabels[value])
    }
    if (value === 'true' || value === 'yes') return t(YES_KEY)
    if (value === 'false' || value === 'no') return t(NO_KEY)
    return String(value)
}

/** Render a filter value (scalar or array) to a single localised, comma-joined string. */
const resolveValue = (entity: string, key: string, value: unknown, t: TranslateFn): string => {
    if (Array.isArray(value)) {
        return value
            .map(item => resolveScalarValue(entity, key, item, t))
            .filter(Boolean)
            .join(', ')
    }
    return resolveScalarValue(entity, key, value, t)
}

/**
 * Localised summary of an export's applied filters, one item per field, ready to render as
 * "field: value" badges. Field names and values reuse the same translations as the entity's
 * table filters. Returns an empty array when there are no filters.
 */
export const summarizeAppliedFilters = (
    entity: string,
    appliedFilters: AppliedFiltersPayload | null | undefined,
    t: TranslateFn
): AppliedFilterSummaryItem[] => {
    if (!appliedFilters || appliedFilters.fields.length === 0) return []
    return appliedFilters.fields
        .map(field => {
            const operatorKey = OPERATOR_LABEL[field.operator]
            const valueless = VALUELESS_OPERATORS.has(field.operator)
            return {
                field: resolveFieldLabel(entity, field.key, t),
                operator: operatorKey ? t(operatorKey).toLowerCase() : '-?-',
                // Valueless operators (empty / not empty) are self-contained; keep them visible.
                value: valueless ? '' : resolveValue(entity, field.key, field.value, t),
                valueless
            }
        })
        .filter(item => item.value !== '' || item.valueless)
        .map(({field, operator, value}) => ({field, operator, value}))
}
